import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Search, ArrowLeft, Download, Loader2 } from "lucide-react";
import { BalanceChart } from "@/components/admin-control/BalanceChart";
import { UserRow, KpiGrid, FeedbackCard } from "@/components/admin-control/Parts";
import { UserDetailSheet } from "@/components/admin-control/UserDetailSheet";
import { toast } from "sonner";

const TABS = [{ key: "all", label: "All Users" }, { key: "premium", label: "Premium" }];

function ExportButton() {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const res = await api.get("/admin/control/export.csv", { responseType: "blob" });
      const name = /filename="([^"]+)"/.exec(res.headers["content-disposition"] || "")?.[1] || "moyo-stars-admin.csv";
      const url = URL.createObjectURL(res.data);
      const a = Object.assign(document.createElement("a"), { href: url, download: name });
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success("Report downloaded");
    } catch { toast.error("Export failed"); } finally { setBusy(false); }
  };
  return (
    <button data-testid="admin-control-export" onClick={run} disabled={busy}
      className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-[#2563EB] hover:bg-[#1D4ED8] text-xs font-semibold text-white transition-colors disabled:opacity-60">
      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} Export CSV
    </button>
  );
}

export default function AdminControl() {
  const [query, setQuery] = useState("");
  const [tab, setTab] = useState("all");
  const [openId, setOpenId] = useState(null);
  const { data: summary } = useQuery({ queryKey: ["admin-control-summary"], queryFn: async () => (await api.get("/admin/control/summary")).data, refetchInterval: 30000 });
  const { data: users = [], isLoading } = useQuery({ queryKey: ["admin-control-users"], queryFn: async () => (await api.get("/admin/control/users")).data, refetchInterval: 30000 });
  const currency = summary?.currency || "GBP";

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return users.filter((u) => (tab === "premium" ? u.is_pro : true))
      .filter((u) => !q || u.name.toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q));
  }, [users, query, tab]);

  return (
    <div data-testid="admin-control" className="min-h-screen bg-[#121214] text-white font-['Manrope',system-ui,sans-serif]">
      <div className="mx-auto w-full max-w-md px-4 py-6 space-y-4">
        <div className="flex items-center justify-between">
          <Link to="/admin" data-testid="admin-control-back" className="inline-flex items-center gap-1.5 text-xs text-zinc-400 hover:text-white transition-colors">
            <ArrowLeft className="h-3.5 w-3.5" /> Admin dashboard
          </Link>
          <ExportButton />
        </div>

        <section className="rounded-2xl bg-[#1A1A1E] p-5 ac-rise">
          <div className="flex items-start justify-between gap-2">
            <h1 className="text-xl font-extrabold tracking-tight">MoYo Stars Admin Control</h1>
            {summary?.test_mode && <span data-testid="admin-control-test-mode" className="shrink-0 text-[10px] font-bold uppercase tracking-wider text-amber-300 border border-amber-300/50 rounded-full px-2 py-0.5">Test mode</span>}
          </div>
          <p data-testid="admin-control-subtitle" className="text-sm text-zinc-400 mt-1">Track your platform financial balances in real time.</p>
          <div className="mt-4">{summary ? <BalanceChart series={summary.series} currency={currency} /> : <div className="h-40 rounded-xl bg-white/5 animate-pulse" />}</div>
          <KpiGrid totalUsers={summary?.total_users ?? "—"} premiumTier={summary?.premium_count ?? "—"} netBalance={summary?.net_balance ?? 0} currency={currency} />
        </section>

        <section className="rounded-2xl bg-[#1A1A1E] p-5 ac-rise" style={{ animationDelay: "80ms" }}>
          <label className="relative block">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
            <input data-testid="admin-control-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search name or email..."
              className="w-full h-11 rounded-xl bg-[#121214] border border-white/10 pl-10 pr-4 text-sm text-white placeholder:text-zinc-500 outline-none focus:border-[#2563EB] transition-colors" />
          </label>
          <div className="flex items-center justify-between mt-4">
            <div data-testid="admin-control-tabs" className="inline-flex rounded-full bg-[#121214] p-1 border border-white/5">
              {TABS.map((t) => (
                <button key={t.key} data-testid={`admin-control-tab-${t.key}`} onClick={() => setTab(t.key)} aria-pressed={tab === t.key}
                  className={`px-4 h-8 rounded-full text-xs font-semibold transition-colors ${tab === t.key ? "bg-[#2563EB] text-white" : "text-zinc-400 hover:text-white"}`}>
                  {t.label}
                </button>
              ))}
            </div>
            <span data-testid="admin-control-listed" className="text-xs text-zinc-500">{rows.length} listed</span>
          </div>
          <ul data-testid="admin-control-user-list" className="mt-2 max-h-[60vh] overflow-y-auto no-scrollbar px-2">
            {rows.map((u) => <UserRow key={u.user_id} u={u} currency={currency} onOpen={(x) => setOpenId(x.user_id)} />)}
          </ul>
          {isLoading && <div className="h-24 rounded-xl bg-white/5 animate-pulse mt-2" />}
          {!isLoading && rows.length === 0 && <p data-testid="admin-control-empty" className="text-sm text-zinc-500 text-center py-6">{query ? `No users match "${query}".` : "No users in this view yet."}</p>}
        </section>

        <section className="rounded-2xl bg-[#1A1A1E] p-5 ac-rise" style={{ animationDelay: "160ms" }}>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-base font-bold">Recent Feedback Stream</h2>
            <Link to="/admin" state={{ tab: "quality" }} data-testid="admin-control-live-reviews" className="text-xs font-semibold text-[#3B82F6] hover:text-[#60A5FA]">Live reviews</Link>
          </div>
          <div className="space-y-3">
            {(summary?.feedback || []).map((f, i) => <FeedbackCard key={f.id || i} f={f} i={i} />)}
            {summary && summary.feedback.length === 0 && <p className="text-sm text-zinc-500 text-center py-4">No written call reviews yet.</p>}
          </div>
        </section>
      </div>
      <UserDetailSheet userId={openId} onClose={() => setOpenId(null)} currency={currency} />
    </div>
  );
}
